import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { tasksApi } from "@/app/lib/api/tasks";
import {
  Task,
  TaskAttachment,
  TaskChecklistItem,
  TaskComment,
  TaskMention,
} from "@/app/lib/supabase/types";
import { useAppStore } from "@/app/lib/stores/useAppStore";
import { createClient } from "@/app/utils/supabase/client";
import { RealtimePostgresChangesPayload } from "@supabase/supabase-js";
import { useAuth } from "@/app/lib/contexts/AuthContext";
import { SUPABASE_TABLES } from "@/app/lib/supabase/supabaseDBConstants";


const TASK_STALE_TIME = 1 * 60 * 1000; // 1 minute
const TASK_GC_TIME = 60 * 60 * 1000; // 1 hour

export const taskQueryKeys = {
  all: ["tasks"] as const,
  byCompany: (companyId: string) =>
    [...taskQueryKeys.all, "company", companyId] as const,
  byProject: (projectId: string) =>
    [...taskQueryKeys.all, "project", projectId] as const,
  byRelatedToId: (relatedToId: string) =>
    [...taskQueryKeys.all, "relatedTo", relatedToId] as const,
  byMentionedId: (mentionedId: string) =>
    [...taskQueryKeys.all, "mentions", mentionedId] as const,
  detail: (taskId: string) =>
    [...taskQueryKeys.all, "detail", taskId] as const,
  attachments: (taskId: string) =>
    [...taskQueryKeys.all, "attachments", taskId] as const,
  checklistItems: (taskId: string) =>
    [...taskQueryKeys.all, "checklistItems", taskId] as const,
  comments: (taskId: string) =>
    [...taskQueryKeys.all, "comments", taskId] as const,
  mentions: (taskId: string) =>
    [...taskQueryKeys.all, "taskMentions", taskId] as const
};

/**
 * Hook to fetch all tasks for the current company with real-time updates
 */
export function useTasks() {
  const queryClient = useQueryClient();
  const { selectedCompanyId, selectedCompanyUser } = useAppStore();
  const { user } = useAuth();

  const query = useQuery<Task[]>({
    queryKey: taskQueryKeys.byCompany(selectedCompanyId || ""),
    queryFn: () => {
      if (!selectedCompanyId) throw new Error("No company selected");
      return tasksApi.getTasks(
        selectedCompanyId,
        user?.id,
        selectedCompanyUser?.role
      );
    },
    enabled: !!selectedCompanyId && !!user,
    staleTime: TASK_STALE_TIME,
    gcTime: TASK_GC_TIME,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!selectedCompanyId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`tasks-${selectedCompanyId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tasks",
          filter: `company_id=eq.${selectedCompanyId}`,
        },
        (payload: RealtimePostgresChangesPayload<Task>) => {
          // Handle different events
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            // Refetch to get the task with all relations
            queryClient.invalidateQueries({
              queryKey: taskQueryKeys.byCompany(selectedCompanyId)
            });

            // Also invalidate project
            const task = payload.new as Task;
            if (task.project_id) {
              queryClient.invalidateQueries({
                queryKey: taskQueryKeys.byProject(task.project_id)
              });
            }
            if (task.related_to_id) {
              queryClient.invalidateQueries({
                queryKey: taskQueryKeys.byRelatedToId(task.related_to_id)
              });
            }
            queryClient.invalidateQueries({
              queryKey: taskQueryKeys.detail(task.id)
            });
          } else if (payload.eventType === "DELETE") {
            // Remove from cache
            queryClient.setQueryData<Task[]>(
              taskQueryKeys.byCompany(selectedCompanyId),
              (old) => old?.filter((t) => t.id !== payload.old?.id) || []
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedCompanyId, queryClient]);

  return query;
}

/**
 * Hook to fetch tasks by project with real-time updates
 */
export function useTasksByProject(projectId: string | undefined) {
  const queryClient = useQueryClient();
  const { selectedCompanyUser } = useAppStore();
  const { user } = useAuth();

  const query = useQuery<Task[]>({
    queryKey: taskQueryKeys.byProject(projectId || ""),
    queryFn: () => {
      if (!projectId) throw new Error("No project ID provided");
      return tasksApi.getTasksByProject(
        projectId,
        user?.id,
        selectedCompanyUser?.role
      );
    },
    enabled: !!projectId && !!user,
    staleTime: TASK_STALE_TIME,
    gcTime: TASK_GC_TIME,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!projectId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`tasks-project-${projectId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tasks",
          filter: `project_id=eq.${projectId}`,
        },
        () => {
          // Refetch to get updated data with relations
          queryClient.invalidateQueries({
            queryKey: taskQueryKeys.byProject(projectId)
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [projectId, queryClient]);

  return query;
}

/**
 * Hook to fetch tasks by production stage with real-time updates
 */
export function useTasksByProductionStage(productionStageId: string | undefined) {
  const queryClient = useQueryClient();
  const { selectedCompanyUser } = useAppStore();
  const { user } = useAuth();

  const query = useQuery<Task[]>({
    queryKey: taskQueryKeys.byRelatedToId(productionStageId || ""),
    queryFn: () => {
      if (!productionStageId) throw new Error("No production stage ID provided");
      return tasksApi.getTasksByProductionStage(
        productionStageId,
        user?.id,
        selectedCompanyUser?.role
      );
    },
    enabled: !!productionStageId && !!user,
    staleTime: TASK_STALE_TIME,
    gcTime: TASK_GC_TIME,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!productionStageId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`tasks-stage-${productionStageId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "tasks",
          filter: `production_stage_id=eq.${productionStageId}`,
        },
        () => {
          // Refetch to get updated data with relations
          queryClient.invalidateQueries({
            queryKey: taskQueryKeys.byRelatedToId(productionStageId)
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [productionStageId, queryClient]);

  return query;
}

/**
 * Hook to fetch tasks where user is mentioned
 */
export function useTasksByMentionedId(mentionedId: string | undefined) {
  return useQuery<Task[]>({
    queryKey: taskQueryKeys.byMentionedId(mentionedId || ""),
    queryFn: () => {
      if (!mentionedId) throw new Error("No mentioned ID provided");
      return tasksApi.getTasksByMentionedId(mentionedId);
    },
    enabled: !!mentionedId,
    staleTime: TASK_STALE_TIME,
    gcTime: TASK_GC_TIME,
  });
}

/**
 * Hook to fetch a single task by ID with real-time updates
 */
export function useTask(taskId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery<Task>({
    queryKey: taskQueryKeys.detail(taskId || ""),
    queryFn: () => {
      if (!taskId) throw new Error("No task ID provided");
      return tasksApi.getTask(taskId);
    },
    enabled: !!taskId,
    staleTime: TASK_STALE_TIME,
    gcTime: TASK_GC_TIME,
  });

  // Set up real-time subscription for this specific task
  useEffect(() => {
    if (!taskId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`task-${taskId}`)
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "tasks",
          filter: `id=eq.${taskId}`,
        },
        () => {
          // Refetch to get updated data with relations
          queryClient.invalidateQueries({
            queryKey: taskQueryKeys.detail(taskId)
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, queryClient]);

  return query;
}

// Task Attachments hooks
export function useTaskAttachments(taskId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery<TaskAttachment[]>({
    queryKey: taskQueryKeys.attachments(taskId || ""),
    queryFn: () => {
      if (!taskId) throw new Error("No task ID provided");
      return tasksApi.getTaskAttachments(taskId);
    },
    enabled: !!taskId,
    staleTime: TASK_STALE_TIME,
    gcTime: TASK_GC_TIME,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!taskId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`task-attachments-${taskId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: SUPABASE_TABLES.TASK_ATTACHMENTS,
          filter: `task_id=eq.${taskId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: taskQueryKeys.attachments(taskId)
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, queryClient]);

  return query;
}

// Task Checklist Items hooks
export function useTaskChecklistItems(taskId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery<TaskChecklistItem[]>({
    queryKey: taskQueryKeys.checklistItems(taskId || ""),
    queryFn: () => {
      if (!taskId) throw new Error("No task ID provided");
      return tasksApi.getTaskChecklistItems(taskId);
    },
    enabled: !!taskId,
    staleTime: TASK_STALE_TIME,
    gcTime: TASK_GC_TIME,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!taskId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`task-checklist-${taskId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: SUPABASE_TABLES.TASK_CHECKLIST_ITEMS,
          filter: `task_id=eq.${taskId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: taskQueryKeys.checklistItems(taskId)
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, queryClient]);

  return query;
}

// Task Comments hooks
export function useTaskComments(taskId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery<TaskComment[]>({
    queryKey: taskQueryKeys.comments(taskId || ""),
    queryFn: () => {
      if (!taskId) throw new Error("No task ID provided");
      return tasksApi.getTaskComments(taskId);
    },
    enabled: !!taskId,
    staleTime: TASK_STALE_TIME, // Comments update frequently
    gcTime: TASK_GC_TIME,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!taskId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`task-comments-${taskId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: SUPABASE_TABLES.TASK_COMMENTS,
          filter: `task_id=eq.${taskId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: taskQueryKeys.comments(taskId)
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, queryClient]);

  return query;
}

// Task Mentions hooks
export function useTaskMentions(taskId: string | undefined) {
  const queryClient = useQueryClient();

  const query = useQuery<TaskMention[]>({
    queryKey: taskQueryKeys.mentions(taskId || ""),
    queryFn: () => {
      if (!taskId) throw new Error("No task ID provided");
      return tasksApi.getTaskMentions(taskId);
    },
    enabled: !!taskId,
    staleTime: TASK_STALE_TIME,
    gcTime: TASK_GC_TIME,
  });

  // Set up real-time subscription
  useEffect(() => {
    if (!taskId) return;

    const supabase = createClient();

    const channel = supabase
      .channel(`task-mentions-${taskId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: SUPABASE_TABLES.TASK_MENTIONS,
          filter: `task_id=eq.${taskId}`,
        },
        () => {
          queryClient.invalidateQueries({
            queryKey: taskQueryKeys.mentions(taskId)
          });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [taskId, queryClient]);

  return query;
}